import React, { useEffect, useRef, useState } from 'react';

interface KakaoMapDrawingProps {
  width?: string;
  height?: string;
  center?: {
    lat: number;
    lng: number;
  };
  level?: number;
  onShapeDrawn?: (shapeData: any) => void;
  onMapReady?: (map: any, drawingManager: any) => void;
}

declare global {
  interface Window {
    kakao: any;
  }
}

const KakaoMapDrawing: React.FC<KakaoMapDrawingProps> = ({
  width = '100%',
  height = '500px',
  center = { lat: 37.5665, lng: 126.978 },
  level = 3,
  onShapeDrawn,
  onMapReady,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [drawingManager, setDrawingManager] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTool, setCurrentTool] = useState<string>('');
  const [coordinates, setCoordinates] = useState<string>('');

  // Load Kakao Maps SDK
  useEffect(() => {
    const apiKey = import.meta.env.VITE_KAKAO_APPKEY;

    if (!apiKey || apiKey === 'your_kakao_app_key_here') {
      setError(
        '카카오맵 API 키가 설정되지 않았습니다. .env.local 파일에 VITE_KAKAO_APPKEY를 설정해주세요'
      );
      setIsLoading(false);
      return;
    }

    // Check if SDK is already loaded
    if (window.kakao && window.kakao.maps) {
      initializeMap();
      return;
    }

    // Load SDK
    const script = document.createElement('script');
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&libraries=drawing&autoload=false`;
    script.async = true;

    script.onload = () => {
      window.kakao.maps.load(() => {
        initializeMap();
      });
    };

    script.onerror = () => {
      setError('카카오맵 SDK 로드에 실패했습니다');
      setIsLoading(false);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts
      const existingScript = document.querySelector(
        `script[src*="dapi.kakao.com"]`
      );
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  const initializeMap = () => {
    if (!mapContainer.current || !window.kakao) return;

    try {
      const options = {
        center: new window.kakao.maps.LatLng(center.lat, center.lng),
        level: level,
      };

      const kakaoMap = new window.kakao.maps.Map(mapContainer.current, options);

      // Initialize drawing manager
      const drawingOptions = {
        map: kakaoMap,
        drawingMode: [
          window.kakao.maps.drawing.OverlayType.MARKER,
          window.kakao.maps.drawing.OverlayType.POLYLINE,
          window.kakao.maps.drawing.OverlayType.POLYGON,
          window.kakao.maps.drawing.OverlayType.RECTANGLE,
          window.kakao.maps.drawing.OverlayType.CIRCLE,
        ],
        guideTooltip: ['draw', 'edit'],
        markerOptions: {
          draggable: true,
          removable: true,
        },
        polylineOptions: {
          draggable: true,
          removable: true,
          editable: true,
          strokeColor: '#FF0000',
          strokeWeight: 3,
          strokeOpacity: 0.8,
        },
        polygonOptions: {
          draggable: true,
          removable: true,
          editable: true,
          strokeColor: '#00FF00',
          strokeWeight: 2,
          strokeOpacity: 0.8,
          fillColor: '#00FF00',
          fillOpacity: 0.3,
        },
        rectangleOptions: {
          draggable: true,
          removable: true,
          editable: true,
          strokeColor: '#0000FF',
          strokeWeight: 2,
          strokeOpacity: 0.8,
          fillColor: '#0000FF',
          fillOpacity: 0.3,
        },
        circleOptions: {
          draggable: true,
          removable: true,
          editable: true,
          strokeColor: '#FF00FF',
          strokeWeight: 2,
          strokeOpacity: 0.8,
          fillColor: '#FF00FF',
          fillOpacity: 0.3,
        },
      };

      const manager = new window.kakao.maps.Drawing.DrawingManager(
        drawingOptions
      );
      setDrawingManager(manager);

      // Add event listeners
      window.kakao.maps.event.addListener(
        manager,
        'drawend',
        (type: any, overlay: any) => {
          console.log('Shape drawn:', type, overlay);
          displayCoordinatesImmediately();
          if (onShapeDrawn) {
            const data = manager.getData();
            onShapeDrawn(data);
          }
        }
      );

      setIsLoading(false);

      if (onMapReady) {
        onMapReady(kakaoMap, manager);
      }

      console.log('Kakao Map with drawing tools initialized successfully');
    } catch (err) {
      console.error('Error initializing Kakao Map:', err);
      setError('지도 초기화에 실패했습니다');
      setIsLoading(false);
    }
  };

  const selectTool = (toolType: string) => {
    if (!drawingManager) return;

    // Cancel current drawing
    drawingManager.cancel();
    setCurrentTool('');

    if (toolType && window.kakao.maps.drawing.OverlayType[toolType]) {
      // Select new tool
      drawingManager.select(window.kakao.maps.drawing.OverlayType[toolType]);
      setCurrentTool(toolType);
    }
  };

  const clearAllShapes = () => {
    if (!drawingManager) return;

    const overlays = drawingManager.getOverlays();

    // Remove all overlays
    Object.keys(overlays).forEach(type => {
      if (overlays[type] && overlays[type].length > 0) {
        overlays[type].forEach((overlay: any) => {
          if (overlay && overlay.setMap) {
            overlay.setMap(null);
          }
        });
      }
    });

    setCoordinates('');
    setCurrentTool('');
  };

  const generateJSON = () => {
    if (!drawingManager) return;

    try {
      const data = drawingManager.getData();
      const jsonString = JSON.stringify(data, null, 2);
      console.log('Generated JSON:', jsonString);
      return jsonString;
    } catch (error) {
      console.error('Error generating JSON:', error);
      return null;
    }
  };

  const displayCoordinatesImmediately = () => {
    if (!drawingManager) return;

    try {
      const data = drawingManager.getData();
      let message = '';

      if (data.marker && data.marker.length > 0) {
        message = 'Marker\n' + JSON.stringify(data.marker, null, 2);
      } else if (data.polyline && data.polyline.length > 0) {
        message = 'Polyline\n' + JSON.stringify(data.polyline, null, 2);
      } else if (data.rectangle && data.rectangle.length > 0) {
        message = 'Rectangle\n' + JSON.stringify(data.rectangle, null, 2);
      } else if (data.circle && data.circle.length > 0) {
        message = 'Circle\n' + JSON.stringify(data.circle, null, 2);
      } else if (data.polygon && data.polygon.length > 0) {
        message = 'Polygon\n' + JSON.stringify(data.polygon, null, 2);
      }

      setCoordinates(message);
    } catch (error) {
      console.error('Error displaying coordinates:', error);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      console.log('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  if (error) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded"
      >
        <div className="text-center p-4">
          <p className="text-red-600 mb-2">⚠️ 지도 오류</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">카카오맵을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Drawing Tools */}
      <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg">
        <button
          onClick={() => selectTool('MARKER')}
          className={`px-3 py-2 text-sm rounded ${
            currentTool === 'MARKER'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Marker
        </button>
        <button
          onClick={() => selectTool('POLYLINE')}
          className={`px-3 py-2 text-sm rounded ${
            currentTool === 'POLYLINE'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Line
        </button>
        <button
          onClick={() => selectTool('POLYGON')}
          className={`px-3 py-2 text-sm rounded ${
            currentTool === 'POLYGON'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Polygon
        </button>
        <button
          onClick={() => selectTool('RECTANGLE')}
          className={`px-3 py-2 text-sm rounded ${
            currentTool === 'RECTANGLE'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Rectangle
        </button>
        <button
          onClick={() => selectTool('CIRCLE')}
          className={`px-3 py-2 text-sm rounded ${
            currentTool === 'CIRCLE'
              ? 'bg-blue-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-100'
          }`}
        >
          Circle
        </button>
        <button
          onClick={clearAllShapes}
          className="px-3 py-2 text-sm rounded bg-red-500 text-white hover:bg-red-600"
        >
          Clear All
        </button>
        <button
          onClick={() => {
            const json = generateJSON();
            if (json) copyToClipboard(json);
          }}
          className="px-3 py-2 text-sm rounded bg-green-500 text-white hover:bg-green-600"
        >
          Copy JSON
        </button>
      </div>

      {/* Map Container */}
      <div
        ref={mapContainer}
        style={{ width, height }}
        className="border border-gray-300 rounded"
      />

      {/* Coordinates Display */}
      {coordinates && (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium text-gray-700">Coordinates</h4>
            <button
              onClick={() => copyToClipboard(coordinates)}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Copy
            </button>
          </div>
          <pre className="text-xs text-gray-600 overflow-auto max-h-32">
            {coordinates}
          </pre>
        </div>
      )}
    </div>
  );
};

export default KakaoMapDrawing;
